// How to Play Modal Component
import React, { useState } from "react";
import {
  HelpCircle,
  X,
  DollarSign,
  Briefcase,
  Users,
  Coffee,
  ChevronLeft,
  ChevronRight,
  Home,
  Star,
  Award,
  AlertCircle,
  Umbrella,
  TrendingUp,
  Building,
  CloudLightning
} from "lucide-react";

const HowToPlayModal = ({ isOpen, onClose }) => {
  const [currentPage, setCurrentPage] = useState(0);
  
  // If modal is not open, don't render anything
  if (!isOpen) return null;
  
  const pages = [
    // Page 1: Game Overview
    {
      title: "Welcome to hotelhero!",
      content: (
        <div>
          <p className="mb-4">
            Welcome to your new career as a hotel manager! Your goal is to build, expand, and manage a successful hotel empire while dealing with the challenges of the hospitality industry.
          </p>
          <div className="grid grid-cols-2 gap-3 mb-4">
            <div className="bg-blue-50 rounded-lg p-3">
              <h4 className="font-bold flex items-center mb-2">
                <DollarSign className="w-4 h-4 mr-2 text-green-600" />
                Make Money
              </h4>
              <p className="text-sm">
                Build rooms, hire staff, and provide amenities to attract guests and earn revenue.
              </p>
            </div>
            <div className="bg-blue-50 rounded-lg p-3">
              <h4 className="font-bold flex items-center mb-2">
                <Star className="w-4 h-4 mr-2 text-yellow-500" />
                Build Reputation
              </h4>
              <p className="text-sm">
                Keep guests happy to improve your rating and attract more high-paying customers.
              </p>
            </div>
          </div>
          <p className="text-sm italic">
            Navigate through this guide using the arrows below to learn the key mechanics and strategies.
          </p>
        </div>
      )
    },
    
    // Page 2: Basic Mechanics
    {
      title: "Game Basics",
      content: (
        <div>
          <div className="grid grid-cols-2 gap-3 mb-4">
            <div className="bg-blue-50 rounded-lg p-3">
              <h4 className="font-bold flex items-center mb-1">
                <Home className="w-4 h-4 mr-2 text-blue-600" />
                Rooms
              </h4>
              <p className="text-sm">
                Build different types of rooms, from Standard to Penthouse. Higher-quality rooms attract wealthier guests.
              </p>
            </div>
            <div className="bg-blue-50 rounded-lg p-3">
              <h4 className="font-bold flex items-center mb-1">
                <Users className="w-4 h-4 mr-2 text-purple-600" />
                Staff
              </h4>
              <p className="text-sm">
                Hire staff to maintain your hotel. Housekeepers maintain room quality, receptionists improve guest happiness.
              </p>
            </div>
            <div className="bg-blue-50 rounded-lg p-3">
              <h4 className="font-bold flex items-center mb-1">
                <Coffee className="w-4 h-4 mr-2 text-amber-600" />
                Facilities
              </h4>
              <p className="text-sm">
                Build facilities like restaurants, bars, and spas to generate additional income and attract specific guest types.
              </p>
            </div>
            <div className="bg-blue-50 rounded-lg p-3">
              <h4 className="font-bold flex items-center mb-1">
                <Award className="w-4 h-4 mr-2 text-yellow-600" />
                Reputation
              </h4>
              <p className="text-sm">
                Keep guests happy to improve your reputation. Higher reputation allows you to charge more for rooms.
              </p>
            </div>
          </div>
          <div className="bg-yellow-50 p-3 rounded-lg border border-yellow-200">
            <h4 className="font-bold mb-1">Game Speed Controls</h4>
            <p className="text-sm">
              Use the speed controls at the top of the screen to pause, run at normal speed, or run at fast speed. Pause when you need to make important decisions!
            </p>
          </div>
        </div>
      )
    },
    
    // Page 3: Success Strategies
    {
      title: "Success Strategies",
      content: (
        <div>
          <div className="space-y-3 mb-4">
            <div className="bg-green-50 rounded-lg p-3 border-l-4 border-green-400">
              <h4 className="font-bold mb-1">Early Game Strategy</h4>
              <p className="text-sm">
                Start by building 3-4 Standard rooms and hiring a Housekeeper. Make sure your rooms are always clean and occupied. Consider firing the starting Receptionist to save money at first.
              </p>
            </div>
            <div className="bg-blue-50 rounded-lg p-3 border-l-4 border-blue-400">
              <h4 className="font-bold mb-1">Mid Game Strategy</h4>
              <p className="text-sm">
                Expand to 8-10 rooms with a mix of Standard and Deluxe. Build a Restaurant and hire more staff. Start marketing campaigns to increase bookings. Monitor your competitors.
              </p>
            </div>
            <div className="bg-purple-50 rounded-lg p-3 border-l-4 border-purple-400">
              <h4 className="font-bold mb-1">Late Game Strategy</h4>
              <p className="text-sm">
                Upgrade to luxury rooms (Suite, Executive, Penthouse). Add high-end facilities like Spa, Casino, or Nightclub. Maintain a full staff roster. Build your market share above 50%.
              </p>
            </div>
          </div>
          <div className="bg-amber-50 p-3 rounded-lg border border-amber-200">
            <h4 className="font-bold flex items-center mb-1">
              <AlertCircle className="w-4 h-4 mr-2 text-amber-600" />
              Balance Is Key
            </h4>
            <p className="text-sm">
              Don't expand too quickly! Make sure you can afford daily staff salaries and maintenance costs before building new rooms or facilities.
            </p>
          </div>
        </div>
      )
    },
    
    // Page 4: Random Events
    {
      title: "Random Events",
      content: (
        <div>
          <p className="mb-3">
            The hotel business is unpredictable! Your hotel will face various random events that can help or hurt your business:
          </p>
          <div className="grid grid-cols-2 gap-3 mb-4">
            <div className="bg-green-50 rounded-lg p-2">
              <h4 className="font-bold flex items-center text-sm mb-1">
                <Briefcase className="w-3 h-3 mr-1 text-blue-600" />
                Business Conference
              </h4>
              <p className="text-xs">
                Brings additional business travelers to your hotel.
              </p>
            </div>
            <div className="bg-green-50 rounded-lg p-2">
              <h4 className="font-bold flex items-center text-sm mb-1">
                <Star className="w-3 h-3 mr-1 text-yellow-600" />
                Celebrity Visit
              </h4>
              <p className="text-xs">
                Increases your hotel's reputation.
              </p>
            </div>
            <div className="bg-red-50 rounded-lg p-2">
              <h4 className="font-bold flex items-center text-sm mb-1">
                <Umbrella className="w-3 h-3 mr-1 text-gray-600" />
                Bad Weather
              </h4>
              <p className="text-xs">
                Causes booking cancellations.
              </p>
            </div>
            <div className="bg-red-50 rounded-lg p-2">
              <h4 className="font-bold flex items-center text-sm mb-1">
                <AlertCircle className="w-3 h-3 mr-1 text-red-600" />
                Maintenance Issues
              </h4>
              <p className="text-xs">
                Requires immediate spending on repairs.
              </p>
            </div>
          </div>
          
          <div className="bg-red-50 p-3 rounded-lg border border-red-200 mb-3">
            <h4 className="font-bold flex items-center mb-1">
              <CloudLightning className="w-4 h-4 mr-2 text-red-600" />
              Major Disasters
            </h4>
            <p className="text-sm">
              As your hotel grows, you'll face increasingly serious challenges like natural disasters, scandals, security incidents, and financial crises. Always maintain a financial safety buffer!
            </p>
          </div>
          
          <p className="text-sm italic">
            Events become more frequent and more severe as the game progresses. Wealthy hotels face the toughest challenges!
          </p>
        </div>
      )
    },
    
    // Page 5: Competition & Marketing
    {
      title: "Competition & Marketing",
      content: (
        <div>
          <div className="mb-4">
            <h4 className="font-bold flex items-center mb-2">
              <Building className="w-4 h-4 mr-2 text-blue-600" />
              Competing Hotels
            </h4>
            <p className="text-sm mb-3">
              You're not the only hotel in town! Monitor your competitors' pricing, facilities, and reputation to stay competitive.
            </p>
            <div className="bg-blue-50 p-3 rounded-lg mb-3">
              <p className="text-sm">
                Your market share shows what percentage of the hotel market you control. Aim to increase this over time.
              </p>
            </div>
          </div>
          
          <div className="mb-4">
            <h4 className="font-bold flex items-center mb-2">
              <TrendingUp className="w-4 h-4 mr-2 text-purple-600" />
              Marketing Campaigns
            </h4>
            <p className="text-sm mb-2">
              Launch marketing campaigns to attract more guests and boost your market share:
            </p>
            <ul className="list-disc pl-5 text-sm">
              <li>Local Advertising - Increases bookings</li>
              <li>Social Media - Boosts reputation</li>
              <li>Discount Promotion - Attracts price-sensitive guests</li>
              <li>Luxury Package - Targets high-end guests</li>
              <li>Competitive Marketing - Steals guests from competitors</li>
            </ul>
          </div>
        </div>
      )
    },
    
    // Page 6: Final Tips
    {
      title: "Final Tips & Reminders",
      content: (
        <div>
          <div className="space-y-3 mb-4">
            <div className="bg-blue-50 rounded-lg p-3">
              <h4 className="font-bold mb-1">Save Your Game</h4>
              <p className="text-sm">
                Remember to save your game regularly to prevent losing progress. Use the Save button at the top of the screen.
              </p>
            </div>
            
            <div className="bg-blue-50 rounded-lg p-3">
              <h4 className="font-bold mb-1">Hotel Policies</h4>
              <p className="text-sm">
                Set policies like pet-friendly rooms, early check-in, and complimentary breakfast to attract specific guest types.
              </p>
            </div>
            
            <div className="bg-blue-50 rounded-lg p-3">
              <h4 className="font-bold mb-1">Seasonal Effects</h4>
              <p className="text-sm">
                The seasons (spring, summer, autumn, winter) affect booking rates and guest preferences. Adapt your strategy accordingly.
              </p>
            </div>
            
            <div className="bg-red-50 rounded-lg p-3">
              <h4 className="font-bold mb-1">Prevent Bankruptcy</h4>
              <p className="text-sm">
                If you go $5,000 in debt, you'll go bankrupt! Always maintain a safety buffer of at least $10,000-$20,000 for emergencies.
              </p>
            </div>
          </div>
          
          <div className="bg-green-100 p-4 rounded-lg text-center">
            <h4 className="font-bold text-lg mb-1">You're Ready!</h4>
            <p className="text-sm">
              Good luck with your hotel empire. Remember, even setbacks are learning opportunities. The path to becoming a hotel tycoon isn't always smooth!
            </p>
          </div>
        </div>
      )
    }
  ];
  
  const nextPage = () => {
    if (currentPage < pages.length - 1) {
      setCurrentPage(currentPage + 1);
    } else {
      onClose(); // Close the modal on the last page
    }
  };
  
  const prevPage = () => {
    if (currentPage > 0) {
      setCurrentPage(currentPage - 1);
    }
  };
  
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-lg max-w-2xl w-full max-h-[80vh] overflow-hidden">
        {/* Header */}
        <div className="flex justify-between items-center bg-blue-600 text-white p-4">
          <h2 className="text-xl font-bold flex items-center">
            <HelpCircle className="mr-2" />
            {pages[currentPage].title}
          </h2>
          <button 
            onClick={onClose}
            className="text-white hover:bg-blue-700 p-1 rounded-full"
          >
            <X className="w-6 h-6" />
          </button>
        </div>
        
        {/* Content */}
        <div className="p-5 overflow-y-auto max-h-[60vh]">
          {pages[currentPage].content}
        </div>
        
        {/* Footer with navigation */}
        <div className="border-t border-gray-200 p-4 flex justify-between items-center bg-gray-50">
          <div className="text-sm text-gray-500">
            Page {currentPage + 1} of {pages.length}
          </div>
          <div className="flex space-x-2">
            <button
              onClick={prevPage}
              disabled={currentPage === 0}
              className={`px-4 py-2 rounded-lg flex items-center ${
                currentPage === 0
                  ? "bg-gray-200 text-gray-400 cursor-not-allowed"
                  : "bg-blue-100 text-blue-600 hover:bg-blue-200"
              }`}
            >
              <ChevronLeft className="w-4 h-4 mr-1" />
              Previous
            </button>
            <button
              onClick={nextPage}
              className="px-4 py-2 rounded-lg bg-blue-600 text-white hover:bg-blue-700 flex items-center"
            >
              {currentPage === pages.length - 1 ? "Finish" : "Next"}
              {currentPage < pages.length - 1 && <ChevronRight className="w-4 h-4 ml-1" />}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default HowToPlayModal;